import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { documentErrorOf } from "../misc/helpers";

const IDLE = { url: "", type: "", loading: false, error: "" };

/**
 * Loads the document behind a /document route into a blob URL for the review
 * modal's frame. A blob URL is same-origin, so the frame's load event is a
 * real signal that the document displayed.
 *
 * `active` is the modal's open state: the bytes are fetched each time it
 * opens and released when it closes, so a reopened contract always shows what
 * the server is serving now — the brand's signature may have landed since.
 * @param options
 */
const useContractPreview = ({ base, path, active }) => {
    const { axiosInstance } = useAxios({ base, showAlert: false });
    const [state, setState] = useState(IDLE);
    // Bumped on an explicit retry, so a failed request can be made again
    // against the same path.
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!active || !path) {
            setState(IDLE);
            return undefined;
        }

        let cancelled = false;
        let objectUrl = null;

        setState({ ...IDLE, loading: true });

        axiosInstance
            .get(path, { responseType: "blob" })
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setState({ ...IDLE, url: objectUrl, type: blob.type || "" });
            })
            .catch(async (error) => {
                const message = await documentErrorOf(error);
                if (!cancelled) setState({ ...IDLE, error: message });
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [base, path, active, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        ...state,
        retry: () => setAttempt((value) => value + 1)
    };
};

export default useContractPreview;
